// src/Navigation.jsx
import React, { useEffect, useState } from 'react';
import { NavLink } from 'react-router-dom';
import './Navbar.css';
import { apiRequest } from './utils';
import translations from './lang/index';
import SideMenu from './SideMenu';

export function Navigation({ isLoggedIn, setIsLoggedIn, username, setUsername }) {
  const [profilePicture, setProfilePicture] = useState(null);
  const [language, setLanguage] = useState(localStorage.getItem('language') || 'ro');
  const [userType, setUserType] = useState(() => (localStorage.getItem('user_type') || null)); // ⬅️ gyorsított első render
  const [isSideOpen, setIsSideOpen] = useState(false);

  const t = translations[language];

  const handleLanguageChange = (lang) => {
    setLanguage(lang);
    localStorage.setItem('language', lang);
    window.location.reload();
  };

  useEffect(() => {
    const storedUsername = localStorage.getItem('username');
    if (storedUsername) setUsername(storedUsername);

    if (isLoggedIn) {
      apiRequest(`${process.env.REACT_APP_API}/api/user-profile/`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      })
        .then((response) => response.json())
        .then((data) => {
          const profilePic = data.user_data?.profile_picture2
            ? `data:image/jpeg;base64,${data.user_data.profile_picture2}`
            : `${process.env.REACT_APP_API}/media/profile_pictures/default.jpg`;
          setProfilePicture(profilePic);

          const uType = (data.user_data?.user_type || '').toLowerCase() || null;
          setUserType(uType);
          localStorage.setItem('user_type', uType || '');
        })
        .catch((error) => {
          console.error('Error fetching profile data:', error);
          // ha valamiért hiba van, ne maradjon régi, félrevezető érték
          setUserType(localStorage.getItem('user_type') || null);
        });
    } else {
      setUserType(null);
      localStorage.removeItem('user_type');
    }
  }, [isLoggedIn, setUsername]);

  const handleLogout = async () => {
    try {
      const refreshToken = localStorage.getItem('refresh_token');
      await fetch(`${process.env.REACT_APP_API}/api/logout/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept-Language': language },
        body: JSON.stringify({ refresh: refreshToken }),
      });
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      localStorage.clear();
      setIsLoggedIn(false);
      setUsername('');
      setUserType(null);
    }
  };

  const showProviderMenus = isLoggedIn && userType === 'provider';
  const showClientMenus   = isLoggedIn && userType === 'client';

  const IconHome = () => (<svg width="22" height="22" viewBox="0 0 24 24"><path d="M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z"/></svg>);
  const IconServices = () => (<svg width="22" height="22" viewBox="0 0 24 24"><path d="M12 2a5 5 0 015 5v2h2a3 3 0 013 3v9a3 3 0 01-3 3H7a3 3 0 01-3-3v-9a3 3 0 013-3h2V7a5 5 0 015-5zm-3 7h6V7a3 3 0 10-6 0v2z"/></svg>);
  const IconCalendar = () => (<svg width="22" height="22" viewBox="0 0 24 24"><path d="M7 2v2H5a2 2 0 00-2 2v2h18V6a2 2 0 00-2-2h-2V2h-2v2H9V2H7zm14 8H3v10a2 2 0 002 2h14a2 2 0 002-2V10z"/></svg>);
  const IconList = () => (<svg width="22" height="22" viewBox="0 0 24 24"><path d="M4 6h2v2H4V6zm4 0h12v2H8V6zM4 11h2v2H4v-2zm4 0h12v2H8v-2zM4 16h2v2H4v-2zm4 0h12v2H8v-2z"/></svg>);
  const IconStats = () => (<svg width="22" height="22" viewBox="0 0 24 24"><path d="M3 13h4v8H3v-8zm7-6h4v14h-4V7zm7-4h4v18h-4V3z"/></svg>);

  return (
    <>
      {/* TOP BAR */}
      <header className="appbar">
        <div className="appbar-left">
          <button
            className={`drawer-btn hamburger ${isSideOpen ? 'open' : ''}`}
            onClick={() => setIsSideOpen(!isSideOpen)}
            aria-label="Open side menu"
            aria-expanded={isSideOpen}
            aria-controls="side-drawer"
            type="button"
          >
            <span></span><span></span><span></span>
          </button>

          <NavLink to="/Home" className="brand"><span className="brand-dot" /> MyServiceHub</NavLink>
        </div>

        <nav className="appbar-center">
          <NavLink to="/Home" className="link" end>{t.home}</NavLink>
          <NavLink to="/services" className="link">{t.services}</NavLink>
          {showProviderMenus && (
            <>
              <NavLink to="/AppointmentsTrai/Appointments" className="link">{t.appointments}</NavLink>
              <NavLink to="/bookings" className="link">{t.bookings}</NavLink>
              <NavLink to="/statistics" className="link">{t.statistics}</NavLink>
              {/* <NavLink to="/users" className="link">{t.users}</NavLink> */}
            </>
          )}
          {showClientMenus && <NavLink to="/bookings" className="link">{t.bookings}</NavLink>}
        </nav>

        <div className="appbar-right">
          {!isLoggedIn ? (
            <div className="auth-actions">
              <NavLink to="/login" className="ghost-btn">{t.login}</NavLink>
              {/* <NavLink to="/register" className="primary-btn">{t.register}</NavLink> */}
            </div>
          ) : null}
        </div>
      </header>

      {/* BOTTOM TAB (mobile) */}
      <nav className="bottom-nav">
        <NavLink to="/Home" className="tab" end>
          <IconHome />
          <span className="tab-label">
            <span className="l1">{t.nav_home_l1}</span>
            <span className="l2">{t.nav_home_l2}</span>
          </span>
        </NavLink>

        <NavLink to="/services" className="tab">
          <IconServices />
          <span className="tab-label">
            <span className="l1">{t.nav_services_l1}</span>
            <span className="l2">{t.nav_services_l2}</span>
          </span>
        </NavLink>

        {showProviderMenus ? (
          <>
            <NavLink to="/AppointmentsTrai/Appointments" className="tab">
              <IconCalendar />
              <span className="tab-label">
                <span className="l1">{t.nav_appointments_l1}</span>
                <span className="l2">{t.nav_appointments_l2}</span>
              </span>
            </NavLink>

            <NavLink to="/bookings" className="tab">
              <IconList />
              <span className="tab-label">
                <span className="l1">{t.nav_bookings_l1}</span>
                <span className="l2">{t.nav_bookings_l2}</span>
              </span>
            </NavLink>

            <NavLink to="/statistics" className="tab">
              <IconStats />
              <span className="tab-label">
                <span className="l1">{t.nav_statistics_l1}</span>
                <span className="l2">{t.nav_statistics_l2}</span>
              </span>
            </NavLink>
          </>
        ) : showClientMenus ? (
          <NavLink to="/bookings" className="tab">
            <IconList />
            <span className="tab-label">
              <span className="l1">{t.nav_bookings_l1}</span>
              <span className="l2">{t.nav_bookings_l2}</span>
            </span>
          </NavLink>
        ) : null}
      </nav>

      {/* SIDE DRAWER */}
      <SideMenu
        isOpen={isSideOpen}
        onClose={() => setIsSideOpen(false)}
        isLoggedIn={isLoggedIn}
        userType={userType}
        username={username}
        profilePicture={profilePicture}
        language={language}
        onLanguageChange={handleLanguageChange}
        onLogout={handleLogout}
      />
    </>
  );
}
